/**
 * Search Dropdown
 *
 * Uses Shopify's Predictive Search API to show live search results
 * as the user types. Shows placeholder content when the search input
 * is empty (popular products, searches, collections, articles).
 */
class SearchDropdown extends HTMLElement {
  constructor() {
    super();

    // UI references
    this.$closeBtn = this.querySelector('.search-dropdown__close');
    this.$predictiveResults = this.querySelector('#predictive-search-results');
    this.$placeholderProducts = this.querySelector('[data-source-section="placeholder-products"]');
    this.$placeholderSidebar = this.querySelector('[data-source-section="placeholder-sidebar"]');

    // State
    this.isOpen = false;
    this.abortController = null;
    this.debounceTimer = null;

    // Search input - find from the parent search form
    this.$searchInput = null;

    // Placeholder typewriter animation
    this.placeholders = (this.dataset.inputPlaceholders || '').split('~').filter(Boolean);
    this.currentPlaceholderIndex = 0;
    this.currentCharIndex = 0;
    this.isDeleting = false;
    this.typewriterTimer = null;

    // GrapheneHC
    this.graphenehcEnabled = this.dataset.graphenehcEnabled === 'true';
  }

  connectedCallback() {
    // Bind close button
    if (this.$closeBtn) {
      this.$closeBtn.addEventListener('click', () => this.close());
    }

    // Find the search input (from the header search form)
    this.$searchInput = document.querySelector('[data-search-input]');

    // Set top position of the dropdown to be equal to the sticky header height
    this.setTopPosition();

    if (this.$searchInput) {
      // Listen for input changes with debounce
      this.$searchInput.addEventListener('input', () => {
        this.debounce(() => this.onInputChange(), 300);
      });

      // Open dropdown on focus
      this.$searchInput.addEventListener('focus', () => {
        this.open();
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Start typewriter animation
    if (this.placeholders.length > 0 && this.$searchInput) {
      this.startTypewriter();
    }
  }

  /**
   * Set top position of the dropdown to be equal to the sticky header height
   */
  setTopPosition() {
    const headerElement = document.querySelector('sticky-header');
    if (!headerElement) return;

    const bounding = headerElement.getBoundingClientRect();
    this.style.top = `${bounding.top + bounding.height}px`;
  }

  /**
   * Handle input changes - fetch predictive search results
   */
  onInputChange() {
    const query = this.$searchInput.value.trim();

    if (query.length === 0) {
      this.showPlaceholders();
      return;
    }

    this.fetchPredictiveSearch(query);
  }

  /**
   * Fetch predictive search results from Shopify's API
   */
  async fetchPredictiveSearch(query) {
    // Exit if GrapheneHC enabled
    if (this.graphenehcEnabled) return;

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product,collection,query,article&resources[limit]=8&resources[limit_scope]=each&resources[options][unavailable_products]=hide`;

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();

      // Parse the section response
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const sectionContent = doc.querySelector('#shopify-section-predictive-search');

      if (sectionContent) {
        const resultsHTML = sectionContent.querySelector('#predictive-search-results');

        if (resultsHTML) {
          this.$predictiveResults.innerHTML = resultsHTML.innerHTML;
          this.hidePlaceholders();
          this.$predictiveResults.classList.remove('hidden');
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Predictive search error:', error);
      }
    }
  }

  /**
   * Show placeholder content (popular products, searches, etc.)
   */
  showPlaceholders() {
    if (this.$placeholderProducts) this.$placeholderProducts.classList.remove('hidden');
    if (this.$placeholderSidebar) this.$placeholderSidebar.classList.remove('hidden');
    if (this.$predictiveResults) {
      this.$predictiveResults.classList.add('hidden');
      this.$predictiveResults.innerHTML = '';
    }
  }

  /**
   * Hide placeholder content
   */
  hidePlaceholders() {
    if (this.$placeholderProducts) this.$placeholderProducts.classList.add('hidden');
    if (this.$placeholderSidebar) this.$placeholderSidebar.classList.add('hidden');
  }

  /**
   * Open the search dropdown
   */
  open() {
    if (this.isOpen) return;
    this.setTopPosition();
    this.isOpen = true;
    this.classList.remove('hidden');
    document.body.classList.add('search-is-open', 'overflow-hidden');
    this.showPlaceholders();
  }

  /**
   * Close the search dropdown
   */
  close() {
    this.isOpen = false;
    this.classList.add('hidden');
    document.body.classList.remove('search-is-open', 'overflow-hidden');

    // Reset input
    if (this.$searchInput) {
      this.$searchInput.value = '';
    }

    // Clear results
    this.showPlaceholders();
  }

  /**
   * Debounce helper
   */
  debounce(fn, wait) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(fn, wait);
  }

  /**
   * Typewriter animation for placeholder text
   */
  startTypewriter() {
    if (!this.placeholders.length || !this.$searchInput) return;

    const tick = () => {
      // Don't animate if focused or has value
      if (document.activeElement === this.$searchInput && this.$searchInput.value.length > 0) {
        this.typewriterTimer = setTimeout(tick, 100);
        return;
      }

      const current = this.placeholders[this.currentPlaceholderIndex];
      const speed = this.isDeleting ? 50 : 100;

      if (!this.isDeleting) {
        // Type forward
        this.currentCharIndex++;
        this.$searchInput.setAttribute('placeholder', current.substring(0, this.currentCharIndex));

        if (this.currentCharIndex === current.length) {
          // Pause at the end before deleting
          this.isDeleting = true;
          this.typewriterTimer = setTimeout(tick, 2000);
          return;
        }
      } else {
        // Delete backward
        this.currentCharIndex--;
        this.$searchInput.setAttribute('placeholder', current.substring(0, this.currentCharIndex));

        if (this.currentCharIndex === 0) {
          this.isDeleting = false;
          this.currentPlaceholderIndex = (this.currentPlaceholderIndex + 1) % this.placeholders.length;
        }
      }

      this.typewriterTimer = setTimeout(tick, speed);
    };

    tick();
  }

  disconnectedCallback() {
    if (this.typewriterTimer) clearTimeout(this.typewriterTimer);
    if (this.abortController) this.abortController.abort();
  }
}

customElements.define('search-dropdown', SearchDropdown);
